import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { depenseService } from '../../services/api';
import { Depense, PayerDepenseData, ModePaiementDepense } from '../../types';

interface PayerDepenseModalProps {
  depense: Depense;
  onSubmit: (data: PayerDepenseData) => void;
  onClose: () => void;
  isLoading?: boolean;
}

const PayerDepenseModal: React.FC<PayerDepenseModalProps> = ({
  depense,
  onSubmit,
  onClose,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<PayerDepenseData>({
    mode_paiement: 'especes',
    reference_paiement: '',
    date_paiement: new Date().toISOString().split('T')[0],
  });

  const { data: modesPaiementData } = useQuery({
    queryKey: ['modes-paiement-depenses'],
    queryFn: () => depenseService.getModesPaiement(),
  });

  const modesPaiement = modesPaiementData?.data || [];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(montant);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <BanknotesIcon className="h-6 w-6 mr-2 text-green-600" />
            Enregistrer le paiement
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Info dépense */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-600">Dépense: <span className="font-medium text-gray-900">{depense.numero}</span></p>
          <p className="text-sm text-gray-600 mt-1">{depense.description}</p>
          <p className="text-lg font-bold text-gray-900 mt-2">
            {formatMontant(depense.montant)} GNF
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Mode de paiement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mode de paiement *
            </label>
            <select
              name="mode_paiement"
              value={formData.mode_paiement}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-700"
              required
            >
              {modesPaiement.map((mode: { value: string; label: string }) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date de paiement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de paiement
            </label>
            <input
              type="date"
              name="date_paiement"
              value={formData.date_paiement}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-700"
            />
          </div>

          {/* Référence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Référence de paiement
            </label>
            <input
              type="text"
              name="reference_paiement"
              value={formData.reference_paiement}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-700"
              placeholder="N° chèque, virement, etc."
            />
          </div>

          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
            >
              {isLoading ? 'Traitement...' : 'Confirmer le paiement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayerDepenseModal;
